import React, { useEffect, useReducer, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { LayoutProvider } from '@hooks/useLayoutState';
import { privateRoutes, getRoutesForUser } from './privateRoutes.config.js';
import { useLocationHistory } from '@hooks/useLastLocation.js';
import userAuth from '@Config/userauth';
import { ProtectedRoute, PublicRoute } from '@Components/ProtectedRoute';
import { publicRoutes } from './publicRoutes.config';
import { Loading } from '@Components';
import { canAccessAdministration } from '@Config/access';
import { SIMULATION_PERMISSIONS_CHANGED } from '@Services/simulationPermissions';
import ToolsPageTransition from '@Containers/Tools/components/ToolsPageTransition';

/**
 * Componente para manejar redirección de mantenimiento
 */
const MaintenanceRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const redirect = import.meta.env.VITE_MAINTENANCE === 'true';
    if (redirect) {
      window.location.href = 'https://www.lifeseguros.com.ar/mantenimiento/';
    }
  }, [navigate]);

  return null;
};

/**
 * Componente para renderizar rutas públicas
 */
const PublicRoutes = () => {
  return (
    <PublicRoute>
      <Routes>
        {publicRoutes.map(({ path, component: Component, exact }) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={<Loading />}>
                <Component />
              </Suspense>
            }
          />
        ))}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </PublicRoute>
  );
};

/**
 * Componente para renderizar rutas privadas según el tipo de usuario
 */
const PrivateRoutes = ({ isAdmin, requirePasswordChange, requireProducerSelection, permissionsVersion }) => {
  const location = useLocation();
  const { getLastLocation } = useLocationHistory();
  const userLastLocation = getLastLocation();

  // Determinar rutas permitidas según el estado del usuario
  const allowedRoutes = getRoutesForUser(isAdmin, requirePasswordChange, requireProducerSelection);

  // Determinar redirección inicial
  const getInitialRedirect = () => {
    if (location.pathname === '/') {
      if (userAuth.isSimulating()) return '/home';
      if (canAccessAdministration()) return '/tools';
      if (requirePasswordChange) return `/recover-reset?code=${userAuth.userCode()}&simulador=true`;
      if (requireProducerSelection) return '/tools/simulate-producer';
      return '/home';
    }
    return null;
  };

  const initialRedirect = getInitialRedirect();

  // Para la ruta /tools/simulate-producer, no requerir selección de productor para evitar bucle infinito
  const shouldRequireProducerSelection = requireProducerSelection && location.pathname !== '/tools/simulate-producer';

  return (
    <ProtectedRoute
      requireAdmin={isAdmin}
      requirePasswordChange={requirePasswordChange}
      requireProducerSelection={shouldRequireProducerSelection}
      allowedRoutes={allowedRoutes.map(route => route.path)}
    >
      <Routes>
        {initialRedirect && (
          <Route path="/" element={<Navigate to={initialRedirect} replace />} />
        )}
        
        {allowedRoutes.map(({ path, component: Component, exact }) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={<Loading />}>
                {path === '/tools' || path.startsWith('/tools/') ? (
                  <ToolsPageTransition key={path} direction={path === '/tools' ? 'left' : 'right'}>
                    <Component permissionsVersion={permissionsVersion} />
                  </ToolsPageTransition>
                ) : (
                  <Component permissionsVersion={permissionsVersion} />
                )}
              </Suspense>
            }
          />
        ))}
        
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </ProtectedRoute>
  );
};

/**
 * Componente principal de routing
 */
export const Routing = () => {
  const [permissionsVersion, refreshPermissions] = useReducer(value => value + 1, 0);
  useEffect(() => {
    window.addEventListener(SIMULATION_PERMISSIONS_CHANGED, refreshPermissions);
    return () => window.removeEventListener(SIMULATION_PERMISSIONS_CHANGED, refreshPermissions);
  }, []);
  const location = useLocation();
  const { saveLastLocation } = useLocationHistory();
  
  const isAuth = userAuth.isAuthenticated();
  const isAdmin = userAuth.isAdmin();
  const requirePasswordChange = userAuth.requestChangePassword();
  const requireProducerSelection = userAuth.requestSelectProducer();

  // Guardar última ubicación para redirecciones
  useEffect(() => {
    saveLastLocation(location);
  }, [location, saveLastLocation]);

  // Configuración del menú (temporalmente vacío hasta implementar estado local)
  const menuConfig = {};

  return (
    <>
      <MaintenanceRedirect />
      <LayoutProvider pathname={location.pathname} menuConfig={menuConfig}>
        {!isAuth ? (
          <PublicRoutes />
        ) : (
          <PrivateRoutes
            permissionsVersion={permissionsVersion}
            isAdmin={isAdmin}
            requirePasswordChange={requirePasswordChange}
            requireProducerSelection={requireProducerSelection}
          />
        )}
      </LayoutProvider>
    </>
  );
};
